import { css } from "@emotion/css";
import { CheckIcon, MinusIcon } from "@octopusdeploy/design-system-icons";
import { borderWidth, borderRadius, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import type { PopoverBasicHelpProps } from "../../Popover";
import { FormDescription } from "../Primitives/FormDescription";
import { InputLabel } from "../Primitives/InputLabel";
import type { DescriptionContent } from "../utils/descriptionWithLinks";
import { CheckboxGroupContext } from "./CheckboxGroupNew";
interface CheckboxNewBaseProps<T extends string | boolean> {
    /**
     * The value of the checkbox button.
     * For singular usage: the current state value
     * For group usage: the value that gets returned when checked
     */
    value: T;
    /**
     * The name of the checkbox group. If not provided, will use the name from CheckboxGroup context.
     */
    name?: string;
    /**
     * onChange handler for singular usage.
     * When provided, this checkbox manages its own state instead of using the context.
     */
    onChange?: (newValue: T) => void;
    /**
     * Controls whether the checkbox button is disabled.
     */
    disabled?: boolean;
    /**
     * Renders the checkbox in the visual “indeterminate” (mixed) state.
     * Set `aria-checked="mixed"`.
     */
    isIndeterminate?: boolean;
    /**
     * Controls whether the checkbox button should be automatically focused.
     */
    autoFocus?: boolean;
}
/** Props when using a visible label (preferred for accessibility). */
interface CheckboxWithLabel<T extends string | boolean> extends CheckboxNewBaseProps<T> {
    /**
     * The label of the checkbox button. Prefer always providing a label for accessibility.
     */
    label: string;
    /**
     * Controls whether the checkbox button should be the default selected option.
     */
    hasDefaultMarker?: boolean;
    /**
     * The description of the checkbox button.
     */
    description?: string | DescriptionContent;
    /**
     * PopoverBasicHelp component to display additional help information
     * displays the Information icon next to the checkbox label
     */
    popover?: React.ReactElement<PopoverBasicHelpProps>;
}
/** Props when the checkbox has no visible label (e.g. in a table row). You must provide an accessible name. */
interface CheckboxWithoutLabel<T extends string | boolean> extends CheckboxNewBaseProps<T> {
    label?: never;
    /**
     * Accessible name for screen readers when no visible label is shown.
     * Required when omitting `label` so the checkbox remains accessible.
     */
    ariaLabel: string;
    description?: never;
    hasDefaultMarker?: never;
    popover?: never;
}
export type CheckboxNewProps<T extends string | boolean> = CheckboxWithLabel<T> | CheckboxWithoutLabel<T>;
/**
 * CheckboxNew component
 */
export function CheckboxNew<T extends string | boolean>(props: CheckboxNewProps<T>) {
    const { description, disabled, isIndeterminate, value, name, autoFocus, hasDefaultMarker, popover, onChange } = props;
    const label = "label" in props && props.label !== undefined ? props.label : undefined;
    const hasLabel = label !== undefined;
    const ariaLabel = hasLabel ? undefined : "ariaLabel" in props ? props.ariaLabel : undefined;
    const context = React.useContext(CheckboxGroupContext);
    const inputRef = React.useRef<HTMLInputElement>(null);
    /* eslint-disable @typescript-eslint/consistent-type-assertions */
    const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        if (onChange) {
            const newValue = isChecked ? (typeof value === "string" ? ("enabled" as T) : (true as T)) : typeof value === "string" ? ("" as T) : (false as T);
            onChange(newValue);
            return;
        }
        if (!context?.onChange)
            return;
        const currentValues = context.value as T[];
        const newValues = isChecked ? [...currentValues, value] : currentValues.filter((v) => v !== value);
        context.onChange(newValues as T[]);
    }, [context, value, onChange]);
    /* eslint-enable @typescript-eslint/consistent-type-assertions */
    const checked = React.useMemo(() => {
        if (onChange) {
            return value;
        }
        if (!context?.value)
            return false;
        return Array.isArray(context.value) && context.value.includes(value);
    }, [context?.value, value, onChange]);
    const isDisabled = disabled || context?.disabled;
    const baseId = React.useId();
    const inputId = `checkbox-${name}-${baseId}`;
    const getAriaDescribedBy = () => {
        const ids = [description && `${inputId}-description`, context?.error && `${context.groupId}-error`].filter(Boolean);
        return ids.length > 0 ? ids.join(" ") : undefined;
    };
    React.useEffect(() => {
        if (inputRef.current) {
            inputRef.current.indeterminate = Boolean(isIndeterminate);
        }
    }, [isIndeterminate]);
    return (<div className={checkboxFieldStyles}>
            <div className={checkboxWrapperStyles}>
                <input ref={inputRef} type="checkbox" name={name || context?.name} value={String(value)} disabled={isDisabled} className={checkboxInputStyles} checked={Boolean(checked)} onChange={handleChange} id={inputId} autoFocus={autoFocus} aria-checked={isIndeterminate ? "mixed" : checked ? "true" : "false"} aria-label={ariaLabel} aria-describedby={!hasLabel ? getAriaDescribedBy() : undefined}/>
                <div className={checkboxIconStyles}>{isIndeterminate ? <MinusIcon size={16} color="currentColor"/> : <CheckIcon size={16} color="currentColor"/>}</div>
            </div>
            {hasLabel && <InputLabel label={label} htmlFor={inputId} isRadioOrCheckbox={true} isDisabled={isDisabled} hasDefaultMarker={hasDefaultMarker} popover={popover} aria-describedby={getAriaDescribedBy()}/>}
            {description && <FormDescription id={`${inputId}-description`} description={description} isDisabled={isDisabled} isRadioOrCheckbox={true}/>}
        </div>);
}
const checkboxButtonSize = "1.25rem";
const checkboxFieldStyles = css({
    display: "grid",
    gridTemplateColumns: `${checkboxButtonSize} auto`,
    columnGap: space[8],
    alignItems: "center",
    margin: 0,
    // Add margin bottom between checkboxes
    "&:has(+ &), & ~ &": {
        marginBottom: space[8],
    },
});
const checkboxWrapperStyles = css({
    position: "relative",
});
const borderWidthValue = borderWidth[1];
const checkboxInputStyles = css({
    display: "grid",
    placeContent: "center",
    margin: 0,
    appearance: "none",
    width: checkboxButtonSize,
    height: checkboxButtonSize,
    backgroundColor: themeTokens.color.background.primary.default,
    border: `${borderWidthValue} solid ${themeTokens.color.border.bold}`,
    borderRadius: borderRadius.extraSmall,
    cursor: "pointer",
    font: "inherit",
    color: "currentcolor",
    "&:before": {
        content: `""`,
        position: "absolute",
        top: 0,
        left: 0,
        width: `calc(100% - (${borderWidthValue} * 2))`,
        height: `calc(100% - (${borderWidthValue} * 2))`,
        backgroundColor: themeTokens.color.background.selected,
        // It needs to be 1px as its nested inside a 2px radius. Have flagged for design token.
        borderRadius: "1px",
        scale: 0,
        translate: "1px 1px",
        transformOrigin: "center center",
        opacity: 0,
        transition: "scale 100ms ease-in, opacity 100ms ease-in",
        "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
        },
    },
    "&:checked:before, &:indeterminate:before": {
        scale: "1",
        opacity: 1,
        transition: "scale 100ms ease-out, opacity 100ms ease-out",
        "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
        },
    },
    "&:checked, &:indeterminate": {
        borderColor: themeTokens.color.border.selected,
    },
    "&:disabled": {
        backgroundColor: themeTokens.color.background.disabled,
        cursor: "not-allowed",
    },
    "&:checked:disabled, &:indeterminate:disabled": {
        borderColor: themeTokens.color.border.bold,
    },
    "&:checked:disabled:before, &:indeterminate:disabled:before": {
        backgroundColor: themeTokens.color.background.disabled,
    },
    "&:focus": {
        outline: `max(2px, 0.15em) solid ${themeTokens.color.border.selected}`,
        outlineOffset: `max(2px, 0.15em)`,
    },
});
const checkboxIconStyles = css({
    display: "grid",
    placeContent: "center",
    position: "absolute",
    top: 0,
    width: checkboxButtonSize,
    height: checkboxButtonSize,
    opacity: 0,
    color: themeTokens.color.icon.inverse.primary,
    pointerEvents: "none",
    transition: "opacity 100ms ease-in-out",
    "@media (prefers-reduced-motion: reduce)": {
        transition: "none",
    },
    "input:checked + &, input:indeterminate + &": {
        opacity: 1,
    },
    "input:disabled:checked + &, input:disabled:indeterminate + &": {
        color: themeTokens.color.icon.tertiary,
    },
});
